import { chacha20poly1305 } from '@noble/ciphers/chacha';
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { randomBytes } from '@noble/hashes/utils';
import { fromBase64url, toBase64url } from './base64url.js';
/** See docs/SETUP-PROTOCOL.md in tellvo-voip-client — the Swift side derives the same bytes. */
export const PROTOCOL_VERSION = 1;
export const SESSION_BYTES = 16;
export const QR_KEY_BYTES = 32;
export const NONCE_BYTES = 12;
export const KEY_BYTES = 32;
const INFO_QR = new TextEncoder().encode('tellvo-setup/v1/qr');
const INFO_CODE = new TextEncoder().encode('tellvo-setup/v1/code');
export function newSession() {
    return randomBytes(SESSION_BYTES);
}
export function newQRKey() {
    return randomBytes(QR_KEY_BYTES);
}
/** Six digits, never starting with a zero so it reads the same spoken and typed. */
export function newPairingCode() {
    const bytes = randomBytes(4);
    const n = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
    return String(100000 + (n % 900000));
}
/** QR mode: both sides hold the 32-byte key from the URL fragment. */
export function deriveKeyFromQR(qrKey, session) {
    assertLength(qrKey, QR_KEY_BYTES, 'qr key');
    assertLength(session, SESSION_BYTES, 'session');
    return hkdf(sha256, qrKey, session, INFO_QR, KEY_BYTES);
}
export function newKeyPair() {
    const secretKey = x25519.utils.randomPrivateKey();
    return { secretKey, publicKey: x25519.getPublicKey(secretKey) };
}
/** Code mode: X25519 agreement, then the same HKDF shape with a different info. */
export function deriveKeyFromCode(secretKey, peerPublicKey, session) {
    assertLength(session, SESSION_BYTES, 'session');
    const shared = x25519.getSharedSecret(secretKey, peerPublicKey);
    return hkdf(sha256, shared, session, INFO_CODE, KEY_BYTES);
}
/** AAD binds a message to its room and its sender. */
export function additionalData(session, from) {
    return new TextEncoder().encode(`${toBase64url(session)}|${from}`);
}
export function seal(key, session, from, plaintext, nonce = randomBytes(NONCE_BYTES)) {
    assertLength(key, KEY_BYTES, 'key');
    assertLength(nonce, NONCE_BYTES, 'nonce');
    const cipher = chacha20poly1305(key, nonce, additionalData(session, from));
    return { n: toBase64url(nonce), c: toBase64url(cipher.encrypt(plaintext)) };
}
export function open(key, session, from, sealed) {
    assertLength(key, KEY_BYTES, 'key');
    const nonce = fromBase64url(sealed.n);
    assertLength(nonce, NONCE_BYTES, 'nonce');
    const cipher = chacha20poly1305(key, nonce, additionalData(session, from));
    return cipher.decrypt(fromBase64url(sealed.c));
}
export function sealJSON(key, session, from, value) {
    return seal(key, session, from, new TextEncoder().encode(JSON.stringify(value)));
}
export function openJSON(key, session, from, sealed) {
    return JSON.parse(new TextDecoder().decode(open(key, session, from, sealed)));
}
function assertLength(bytes, expected, what) {
    if (bytes.length !== expected)
        throw new Error(`${what}: expected ${expected} bytes, got ${bytes.length}`);
}
//# sourceMappingURL=crypto.js.map